import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  LogOut,
  Calculator,
  Scale,
  Scissors,
  Building2,
  ChefHat,
  Stethoscope,
  Factory,
  BarChart3,
  TrendingUp,
  Users,
  Activity,
  Clock,
  Target,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  FileText,
  Calendar,
  Zap,
  Brain,
  Star,
  MessageCircle,
  Scan
} from 'lucide-react'

// Importar los componentes de dashboards específicos
import ContableDashboard from './dashboards/ContableDashboard'
import JuridicoDashboard from './dashboards/JuridicoDashboard'
import SalonDashboard from './dashboards/SalonDashboard'
import RegidorDashboard from './dashboards/RegidorDashboard'
import RestauranteDashboard from './dashboards/RestauranteDashboard'
import ClinicaDashboard from './dashboards/ClinicaDashboard'
import ManufactureraDashboard from './dashboards/ManufactureraDashboard'

// Importar los nuevos POCs
import CustomerServiceDemo from './CustomerServiceDemo'
import DocumentAutomationDemo from './DocumentAutomationDemo'

export default function AnalyticsDemo({ onLogout }) {
  const [selectedBusiness, setSelectedBusiness] = useState(null)

  const businessTypes = [
    {
      id: 'contable',
      title: 'Despacho Contable',
      description: 'AI para automatización fiscal, conciliaciones y compliance',
      icon: Calculator,
      color: 'from-blue-500 to-blue-700',
      metrics: ['Automatización 85%', 'Ahorro 60h/mes', 'Precisión 99.2%'],
      component: ContableDashboard
    },
    {
      id: 'juridico',
      title: 'Despacho Jurídico',
      description: 'Análisis de documentos legales y gestión de casos con IA',
      icon: Scale,
      color: 'from-purple-500 to-purple-700',
      metrics: ['Análisis docs +70%', 'Investigación -50%', 'Precisión 94%'],
      component: JuridicoDashboard
    },
    {
      id: 'salon',
      title: 'Salón de Belleza',
      description: 'Recomendaciones personalizadas y análisis de preferencias',
      icon: Scissors,
      color: 'from-pink-500 to-pink-700',
      metrics: ['Satisfacción 96%', 'Ventas +35%', 'Retención 88%'],
      component: SalonDashboard
    },
    {
      id: 'regidor',
      title: 'Regidor Municipal',
      description: 'Análisis de gestión pública y atención ciudadana',
      icon: Building2,
      color: 'from-green-500 to-green-700',
      metrics: ['Eficiencia +45%', 'Satisfacción 82%', 'Respuesta 24h'],
      component: RegidorDashboard
    },
    {
      id: 'restaurante',
      title: 'Restaurante',
      description: 'Optimización de menús, inventario y experiencia del cliente',
      icon: ChefHat,
      color: 'from-orange-500 to-orange-700',
      metrics: ['Margen +28%', 'Desperdicio -40%', 'Satisfacción 94%'],
      component: RestauranteDashboard
    },
    {
      id: 'clinica',
      title: 'Clínica Médica',
      description: 'Análisis predictivo de salud y gestión de pacientes',
      icon: Stethoscope,
      color: 'from-red-500 to-red-700',
      metrics: ['Precisión 97%', 'Tiempo -35%', 'Satisfacción 91%'],
      component: ClinicaDashboard
    },
    {
      id: 'manufacturera',
      title: 'Empresa Manufacturera',
      description: 'Optimización de procesos y mantenimiento predictivo',
      icon: Factory,
      color: 'from-gray-500 to-gray-700',
      metrics: ['Eficiencia +32%', 'Downtime -55%', 'Calidad 99.1%'],
      component: ManufactureraDashboard
    }
  ]

  const selectedBusinessData = businessTypes.find(b => b.id === selectedBusiness)

  if (selectedBusiness && selectedBusinessData) {
    const DashboardComponent = selectedBusinessData.component
    return (
      <DashboardComponent 
        onBack={() => setSelectedBusiness(null)}
        onLogout={onLogout}
        businessData={selectedBusinessData}
      />
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Link>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center mr-3">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Analytics Dashboard</h1>
                  <p className="text-sm text-gray-500">Dashboards AI para Diferentes Negocios</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Intro Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Dashboards AI para Diferentes Industrias
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explora cómo la Inteligencia Artificial puede transformar la gestión y análisis de datos 
            en diferentes tipos de negocios. Cada dashboard muestra métricas específicas y casos de uso reales.
          </p>
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
        >
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Brain className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Industrias</p>
                <p className="text-2xl font-bold text-gray-900">7+</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Eficiencia Promedio</p>
                <p className="text-2xl font-bold text-gray-900">+45%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Automatización</p>
                <p className="text-2xl font-bold text-gray-900">85%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Star className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Satisfacción</p>
                <p className="text-2xl font-bold text-gray-900">92%</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Business Types Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {businessTypes.map((business, index) => {
            const IconComponent = business.icon
            
            return (
              <motion.div
                key={business.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden group"
                onClick={() => setSelectedBusiness(business.id)}
              >
                {/* Card Header */}
                <div className={`h-32 bg-gradient-to-br ${business.color} relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black bg-opacity-10 group-hover:bg-opacity-5 transition-all duration-300" />
                  <div className="relative h-full flex items-center justify-center">
                    <IconComponent className="w-12 h-12 text-white group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-white bg-opacity-20 text-white px-2 py-1 rounded-full text-xs font-medium">
                      Demo
                    </span>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {business.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {business.description}
                  </p>

                  {/* Metrics */}
                  <div className="space-y-2">
                    {business.metrics.map((metric, metricIndex) => (
                      <div key={metricIndex} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-600">{metric}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-600 group-hover:text-blue-700 transition-colors">
                        Ver Dashboard
                      </span>
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <ArrowLeft className="w-3 h-3 text-blue-600 rotate-180" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8"
        >
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              ¿Por qué AI Analytics?
            </h3>
            <p className="text-gray-600 mb-6 max-w-3xl mx-auto">
              Cada dashboard está diseñado específicamente para las necesidades de cada industria, 
              mostrando cómo la IA puede optimizar procesos, reducir costos y mejorar la toma de decisiones.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Tiempo Real</h4>
                <p className="text-sm text-gray-600">Métricas actualizadas automáticamente</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Personalizado</h4>
                <p className="text-sm text-gray-600">Adaptado a cada tipo de negocio</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Automático</h4>
                <p className="text-sm text-gray-600">Insights generados por IA</p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  )
} 